import { useEffect, useState } from "react";
import { Loader2, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Problem, detail, field, ghost, primary } from "./ui";

const BDG = { name: "Beall Digital Group", organisation: "Beall Digital Group", email: "", telephone: "", address: "", city: "", sp: "", pc: "", cc: "US" };
const FIELDS = [["name", "Full name"], ["organisation", "Organisation"], ["email", "Email"], ["telephone", "Phone (+1.5555550100)"], ["address", "Street address"], ["city", "City"], ["sp", "State"], ["pc", "Postcode"], ["cc", "Country (US)"]];

const ContactForm = ({ c, set }) => (
  <div className="grid gap-2 sm:grid-cols-3">
    {FIELDS.map(([k, l]) => <input key={k} value={c[k]} onChange={(e) => set({ ...c, [k]: k === "cc" ? e.target.value.toUpperCase() : e.target.value })} placeholder={l} aria-label={l} data-testid={`register-contact-${k}`} className={field} />)}
  </div>
);

const tldOf = (domain, prices) => {
  const parts = domain.split(".");
  for (let i = 1; i < parts.length; i += 1) { const t = `.${parts.slice(i).join(".")}`; const p = prices.find((x) => x.tld === t); if (p) return { tld: t, p }; }
  return { tld: `.${parts.slice(1).join(".")}`, p: null };
};
const money2 = (n) => Math.round(n * 100) / 100;

const Register = ({ pick, clients, prices, done }) => {
  const [who, setWho] = useState("bdg");
  const [clientId, setClientId] = useState("");
  const [billTo, setBillTo] = useState("");
  const [c, setC] = useState(BDG);
  const [years, setYears] = useState(1);
  const { tld, p: saved } = tldOf(pick.domain, prices);
  const [price, setPrice] = useState(saved ? String(saved.cost) : "");
  const [key, setKey] = useState("");
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  useEffect(() => { api.get("/admin/twentyi/idempotency-key").then((r) => setKey(r.data.key)); }, [pick.domain]);
  useEffect(() => { if (saved) setPrice(String(money2(saved.cost * Number(years)))); }, [years, saved?.cost]); // eslint-disable-line react-hooks/exhaustive-deps
  const chooseClient = (id) => { setClientId(id); setBillTo(id); const x = clients.find((k) => k.id === id); if (x) setC({ ...BDG, organisation: "", name: x.name, email: x.email }); };
  const amount = Number(price);
  const clientTotal = saved ? money2(saved.client_price * Number(years)) : null;
  const missing = [
    c.name.trim().length < 2 && "name", !/^\S+@\S+\.\S+$/.test(c.email) && "email", !/^\+\d{1,3}\.\d{4,14}$/.test(c.telephone) && "phone as +1.5555550100",
    c.address.trim().length < 3 && "address", c.city.trim().length < 2 && "city", c.pc.trim().length < 2 && "postcode", !/^[A-Z]{2}$/.test(c.cc) && "2-letter country",
  ].filter(Boolean);
  const phrase = `Confirm purchase — $${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} will be charged to your 20i account`;
  const buy = async () => {
    setBusy(true); setFail(null);
    try {
      const r = await api.post("/admin/twentyi/domains/register", { domain: pick.domain, years: Number(years), price: amount, confirm_text: phrase, idempotency_key: key, registrant: who, client_id: who === "client" ? clientId : null, bill_to_client_id: billTo || null, contact: c });
      toast.success(r.data.duplicate ? r.data.message : `${pick.domain} registered`);
      if (r.data.invoice) toast.info(`Draft invoice created${r.data.invoice.flag ? ` — ${r.data.invoice.flag}` : ""}`); else if (r.data.invoice_note) toast.info(r.data.invoice_note);
      done();
    } catch (e) { setFail(detail(e)); setStep("form"); }
    setBusy(false);
  };
  return (
    <div data-testid="register-form" className="mt-4 space-y-3 rounded-xl border border-white/10 p-4">
      <p className="font-semibold text-white">Register <span data-testid="register-domain">{pick.domain}</span></p>
      <div className="flex flex-wrap gap-2">
        <select value={who} onChange={(e) => { setWho(e.target.value); setC(BDG); }} data-testid="register-registrant-select" className={`${field} w-auto`}>
          <option value="bdg">Registrant: Beall Digital Group</option><option value="client">Registrant: a client</option>
        </select>
        {who === "client" && <select value={clientId} onChange={(e) => chooseClient(e.target.value)} data-testid="register-client-select" className={`${field} w-auto`}>
          <option value="">Choose client</option>{clients.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>}
        <select value={years} onChange={(e) => setYears(e.target.value)} data-testid="register-years-select" className={`${field} w-auto`}>{(pick.years || [1]).map((y) => <option key={y} value={y}>{y} year{y > 1 ? "s" : ""}</option>)}</select>
        <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" min="0" step="0.01" placeholder="20i cost (USD)" aria-label="20i cost" data-testid="register-price-input" className={`${field} w-36`} />
        <select value={billTo} onChange={(e) => setBillTo(e.target.value)} data-testid="register-billto-select" className={`${field} w-auto`}>
          <option value="">Bill to: nobody (no invoice)</option>{clients.map((k) => <option key={k.id} value={k.id}>Bill to: {k.name}</option>)}
        </select>
      </div>
      {saved ? (
        <p data-testid="register-price-summary" className="text-sm text-slate-300">20i cost <span data-testid="register-cost">${amount.toFixed(2)}</span> · client price <span data-testid="register-client-price">${clientTotal.toFixed(2)}</span> · profit <span data-testid="register-profit" className="text-emerald-300">${money2(clientTotal - amount).toFixed(2)}</span></p>
      ) : <p data-testid="register-no-saved-price" className="text-sm text-amber-200">No saved price for {tld}</p>}
      <p className="text-xs text-slate-400">Check the cost against My20i → Domains (20i's API has no price lookup). The cost is charged to Beall Digital Group's 20i account; the bill-to client gets a Draft invoice at the client price to review before sending.</p>
      <ContactForm c={c} set={setC} />
      {missing.length > 0 && <p data-testid="register-missing" className="text-xs text-amber-200">Still needed: {missing.join(", ")}</p>}
      {step === "form" ? (
        <button onClick={() => setStep("confirm")} disabled={!(amount > 0) || (who === "client" && !clientId) || missing.length > 0} data-testid="register-review-btn" className={ghost}>Review purchase</button>
      ) : (
        <div data-testid="register-confirm-box" className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-amber-100">
          <p>{pick.domain} · {years} year(s) · registrant {c.name} · {billTo ? `draft invoice to ${clients.find((k) => k.id === billTo)?.name}` : "No client assigned — no invoice created"}</p>
          <div className="mt-2 flex gap-2">
            <button onClick={buy} disabled={busy} data-testid="register-confirm-btn" className={`${primary} bg-amber-400 hover:bg-amber-300`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}{phrase}</button>
            <button onClick={() => setStep("form")} data-testid="register-cancel-btn" className={ghost}>Cancel</button>
          </div>
        </div>
      )}
      <Problem d={fail} testId="register-error" />
    </div>
  );
};

export const DomainsPanel = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  const [pick, setPick] = useState(null);
  const [clients, setClients] = useState([]);
  const [hist, setHist] = useState([]);
  const [prices, setPrices] = useState([]);
  const loadHist = () => api.get("/admin/twentyi/domains/purchases").then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/twentyi/contacts").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/twentyi/prices").then((r) => setPrices(r.data)).catch(() => {});
    loadHist();
  }, []);
  const search = async (e) => {
    e.preventDefault(); setBusy(true); setFail(null); setPick(null);
    try { setRes((await api.get("/admin/twentyi/domains/search", { params: { q } })).data); } catch (er) { setFail(detail(er)); }
    setBusy(false);
  };
  return (
    <div data-testid="domains-panel">
      <form onSubmit={search} className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a name, e.g. joesbakery" data-testid="domain-search-input" className={field} />
        <button disabled={busy || q.length < 2} data-testid="domain-search-btn" className={primary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Search</button>
      </form>
      <Problem d={fail} testId="domain-search-error" />
      {res && <p data-testid="domain-pricing-note" className="mt-3 text-xs text-slate-400">{res.pricing.note}</p>}
      {res && (
        <ul data-testid="domain-results" className="mt-3 grid gap-2 sm:grid-cols-2">
          {res.results.map((r) => (
            <li key={r.domain} data-testid={`domain-result-${r.domain}`} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-sm">
              <span className="text-white">{r.domain}</span>
              {r.available ? <button onClick={() => setPick(r)} data-testid={`domain-select-${r.domain}`} className="text-cyan-200 underline">Register</button> : <span className="text-slate-500">{r.can || "taken"}</span>}
            </li>
          ))}
        </ul>
      )}
      {pick && <Register key={pick.domain} pick={pick} clients={clients} prices={prices} done={() => { setPick(null); loadHist(); }} />}
      {hist.length > 0 && (
        <div className="mt-6"><p className="text-xs uppercase tracking-wider text-slate-500">Purchases</p>
          <ul data-testid="domain-purchases" className="mt-2 space-y-1 text-sm text-slate-300">{hist.map((h) => <li key={h.key}>{h.domain} · ${h.price} · {h.status}{h.expiry ? ` · expires ${h.expiry}` : ""}</li>)}</ul>
        </div>
      )}
    </div>
  );
};
